import React, { useEffect, useRef, useState } from 'react';
import CustomButton from '../components-library/buttons/CustomButton';
import { Trans, useTranslation } from 'react-i18next';
import useAuth from '../hooks/global/useAuth';
import useAppState from '../hooks/global/useAppState';
import useRouter from '../hooks/navigation/useRouter';
import useAppMethods from '../hooks/utility/useAppMethods';
import OtpFlow, { OtpFlowHandle } from '../components-library/auth/OtpFlow';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';
import { StoreInterface } from '../store/types';
import { Drawer } from '@mui/material';
import CloseCircleIcon from '../assets/icons/CloseCircleIcon';
import PromoCodeIcon from '../assets/icons/promotion/PromoCodeIcon';
import StarOutlineIcon from '../assets/icons/promotion/StarOutlineIcon';
import AlarmClockIcon from '../assets/icons/promotion/AlarmClockIcon';
import { Link } from 'react-router-dom';
import { SCREENS } from '../services/exports/Constants';
import PhoneNumberInput from '../components-library/PhoneNumberInput';
import useOrderFlow from '../hooks/order/useOrderFlow';
import { EmailField, FirstNameField, LastNameField } from '../components-library/checkout/sections/CustomerInfoSection';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import useData from '../hooks/utility/useData';
import ProfileManager from '../services/api/ProfileManager';
import moment from 'moment';
import MarketingSubscriptionConsent from '../components-library/marketing/MarketingSubscriptionConsent';
import useScreenType from '../hooks/utility/useScreenType';
import useTheme from '../hooks/ui/useTheme';

enum Steps {
  Otp = 'otp',
  Profile = 'profile',
}

const LoginModal = () => {
  const { t, i18n } = useTranslation(null, { keyPrefix: 'Modals:LoginModal' });

  const { company } = useSelector((state: StoreInterface) => state.initialData);

  const { openModal, title, isStatic, initialPhoneNumber, onCloseRedirect } = useAppState().loginModalProps;

  const { toggleLoginModal } = useAppMethods();
  const { navigate, generatePath } = useRouter();
  const { isLoggedIn } = useAuth();
  const { localData } = useOrderFlow();
  const { isDesktop } = useScreenType();
  const theme = useTheme();

  const [step, setStep] = useState(Steps.Otp);
  const [loading, setLoading] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState(null);
  const [otpRequestedFor, setOtpRequestedFor] = useState(null);
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    if (step === Steps.Otp && otpRequestedFor && isComplete) {
      _onSubmit();
    }
  }, [isComplete]);

  const initialData = {
    first_name: localData.customer_first_name,
    last_name: localData.customer_last_name,
    email: localData.customer_email,
    birthday: null,
    is_marketing_subscriber: false,
    lang: i18n.language,
  };

  const { data, setData, update } = useData(initialData);

  const otpFlowRef = useRef<OtpFlowHandle>(null);
  const bodyRef = useRef(null);

  const _onSubmit = async () => {
    setLoading(true);
    const handler = {
      [Steps.Otp]: async () => (otpRequestedFor !== phoneNumber ? await onRequestOtp() : await onLogIn()),
      [Steps.Profile]: async () => await onUpdateProfile(),
    }[step];

    await handler();
    setLoading(false);
  };

  const onRequestOtp = async () => {
    await otpFlowRef.current.requestCode();
  };

  const onLogIn = async () => {
    const { success, response } = await otpFlowRef.current.logIn({
      company_id: company?.id,
    });

    if (!success) {
      return toast.error(t('toasts.invalid_code'));
    }

    if (
      Boolean(response.data.user.email) &&
      Boolean(response.data.user.first_name) &&
      Boolean(response.data.user.last_name) &&
      Boolean(response.data.user.is_marketing_subscriber)
    ) {
      return closeModal(true);
    }

    setData((current) => ({
      ...current,
      first_name: localData.customer_first_name ?? response.data.user.first_name,
      last_name: localData.customer_last_name ?? response.data.user.last_name,
      email: localData.customer_email ?? response.data.user.email,
      birthday: response.data.user.birthday ? moment(response.data.user.birthday).utc(true) : null,
      is_marketing_subscriber: Boolean(response.data.user.is_marketing_subscriber),
    }));

    return setStep(Steps.Profile);
  };

  const onUpdateProfile = async () => {
    const { success } = await ProfileManager.putProfile(company?.id, data);

    if (!success) {
      return toast.error(t('toasts.failed_to_update_profile'));
    }

    return closeModal(true);
  };

  function resetState() {
    setStep(Steps.Otp);
    setLoading(false);
    setPhoneNumber(null);
    setOtpRequestedFor(null);
    setIsComplete(false);
    setData(initialData);
  }

  function closeModal(success?: boolean): void {
    toggleLoginModal({ openModal: false });
    resetState();

    if (onCloseRedirect) {
      return onCloseRedirect(isLoggedIn || success);
    }

    if (!isLoggedIn) {
      navigate({
        web: {
          to: -1,
        },
      });
    }
  }

  const backdrop = theme.isDarkMode
    ? {
        backgroundColor: 'rgba(52, 52, 52, 0.5)',
        backdropFilter: 'blur(4px)',
      }
    : {};

  return (
    <Drawer
      open={openModal}
      anchor="right"
      onClose={() => closeModal()}
      classes={{
        paper: 'lg:max-w-2/5',
      }}
      slotProps={{
        backdrop: {
          sx: backdrop,
        },
      }}
      ModalProps={{
        disableEnforceFocus: true,
      }}
    >
      <div className="h-full flex flex-col justify-between bg-brand-background-primary">
        <div className="lg:px-lg px-md pt-lg" ref={bodyRef}>
          <div className="flex justify-between">
            {title ?? <h6 className="my-auto">{t('title')}</h6>}
            <button onClick={() => closeModal()} className="my-auto">
              <CloseCircleIcon />
            </button>
          </div>
          {step === Steps.Otp && (
            <OtpFlow
              ref={otpFlowRef}
              isStatic={isStatic}
              initialPhoneNumber={initialPhoneNumber}
              onPhoneNumberChange={setPhoneNumber}
              onValidationStatusChange={(result: boolean) => setIsComplete(result)}
              onCodeRequested={(phone: string) => setOtpRequestedFor(phone)}
              className="mt-lg"
              phoneNumberLabel={
                <>
                  <h6 className="mb-xs font-medium">{t('form.phone_number.label')}</h6>
                  <p className="text-xs">{t('form.phone_number.description')}</p>
                </>
              }
            />
          )}
          {step === Steps.Profile && (
            <div className="mt-lg">
              <label htmlFor="phone_number">
                <h6 className="mb-xs font-medium">{t('form.phone_number.label')}</h6>
                <p className="text-xs">{t('form.phone_number.description')}</p>
              </label>
              <PhoneNumberInput
                placeholder={t('form.phone_number.placeholder')}
                disabled
                value={phoneNumber}
                onChange={setPhoneNumber}
                id="phone_number"
                className="mt-xs"
              />
              <EmailField value={data.email} onChange={(value: string) => update('email', value)} className="mt-md" />
              <div className="grid grid-cols-2 mt-md gap-xs">
                <FirstNameField value={data.first_name} onChange={(value: string) => update('first_name', value)} />
                <LastNameField value={data.last_name} onChange={(value: string) => update('last_name', value)} />
              </div>
              <div className="mt-md">
                <label htmlFor="birthday" className="text-sm">
                  {t('form.birthday.label')}
                </label>
                <div className="mt-md">
                  <DatePicker
                    value={data.birthday}
                    format="DD/MM"
                    localeText={{
                      fieldDayPlaceholder: () => t('form.birthday.parts.day'),
                      fieldMonthPlaceholder: () => t('form.birthday.parts.month'),
                    }}
                    onChange={(value) => update('birthday', value ? value.utc(true) : null)}
                    openTo="month"
                    views={['month', 'day']}
                    className="w-full"
                    label={t('form.birthday.placeholder')}
                  />
                </div>
              </div>
              <MarketingSubscriptionConsent
                checked={data.is_marketing_subscriber}
                onChange={(checked) => update('is_marketing_subscriber', checked)}
                className="mt-md"
              />
            </div>
          )}
        </div>
        <div className="sticky bottom-0 bg-gray dark:bg-black lg:px-lg px-md py-lg mt-xl z-50">
          {isDesktop && (
            <div className="grid grid-cols-3 gap-md">
              <div className="flex flex-col items-center">
                <StarOutlineIcon className="h-8 w-8" />
                <p className="text-xs mt-xs text-center">{t('benefits.earn_points')}</p>
              </div>
              <div className="flex flex-col items-center">
                <PromoCodeIcon className="h-8 w-8" />
                <p className="text-xs mt-xs text-center">{t('benefits.get_deals')}</p>
              </div>
              <div className="flex flex-col items-center">
                <AlarmClockIcon className="h-8 w-8" />
                <p className="text-xs mt-xs text-center">{t('benefits.redeem_points')}</p>
              </div>
            </div>
          )}
          <CustomButton
            title={t('buttons.continue')}
            loading={loading}
            disabled={!isComplete}
            onClick={_onSubmit}
            className="mt-md disabled:bg-gray-700"
            showArrow
          />
          <p className="mt-md text-sm text-brand-text-secondary">
            <Trans
              t={t}
              components={{
                TermsLink: (
                  <Link
                    to={generatePath(SCREENS.TERMS_AND_CONDITIONS)}
                    target="_blank"
                    className="underline text-brand-primary"
                  />
                ),
                PrivacyPolicyLink: (
                  <Link
                    to={generatePath(SCREENS.PRIVACY_POLICY)}
                    target="_blank"
                    className="underline text-brand-primary"
                  />
                ),
              }}
            >
              terms
            </Trans>
          </p>
        </div>
      </div>
    </Drawer>
  );
};

export default LoginModal;
